import React, { useEffect, useState } from "react"
import { graphql, navigate } from 'gatsby';
import { Auth } from "@aws-amplify/auth";
import { authStorage, sessionReceiverURL } from '@services/amplifyStorage';
import { Alert } from '@lesmills-international/components';
import Layout from '../components/common/layout';
import { Wrapper } from '../components/signup/createAccount/style';
import { setSessionStorage, getSessionStorage, getParamsFromURL, RESELLER_ACCESS_ID } from '../utils/utilities';
import ROUTES from '../../../../src/constants/routes';

const client = () =>
  Auth.configure({
    storage: authStorage,
    region: process.env.GATSBY_AWS_COGNITO_REGION,
    userPoolId: process.env.GATSBY_AWS_COGNITO_USER_POOL_ID,
    userPoolWebClientId: process.env.GATSBY_AWS_COGNITO_USER_POOL_WEB_CLIENT_ID,
    graphql_endpoint_iam_region: process.env.GATSBY_AWS_COGNITO_REGION,
    graphql_endpoint: process.env.GATSBY_APP_SYNC_URL,
    ...{
      Auth: {
        storage: authStorage,
      },
    },
    oauth: {
      domain: process.env.GATSBY_AWS_COGNITO_URL,
      scope: ["email", "openid", "profile"],
      redirectSignIn: process.env.GATSBY_AWS_OAUTH_REDIRECT_SIGN_IN,
      redirectSignOut: process.env.GATSBY_AWS_OAUTH_REDIRECT_SIGN_OUT,
      responseType: "code",
    },
    federationTarget: "COGNITO_USER_POOLS",
  })

interface IError {
  [key: string]: {
    text: string;
  }
}

const FederatedVerification = ({ pageContext, data }) => {
  const [syncSessionReady, setSyncSessionReady] = useState(false);
  const [error, setError] = useState<IError | null>(null);
  const { lang } = pageContext;
  const { prismicSignUpLayout, prismicFederatedVerificationPage: { data: fvData } } = data;

  const resellerAccessId = getParamsFromURL("reseller")
  if (resellerAccessId) {
    setSessionStorage(RESELLER_ACCESS_ID, resellerAccessId)
  }

  // Init AWS Amplify
  useEffect(() => {
    if (!syncSessionReady) {
      return
    }
    client()
    Auth.currentAuthenticatedUser({
      bypassCache: false, // Optional, By default is false. If set to true, this call will send a request to Cognito to get the latest user data
    })
      .then(res => {
        const identities = res.signInUserSession
          ? res.signInUserSession.idToken.payload.identities
          : {}
        if (identities && identities.length && identities[0].providerType === "OIDC") {
          navigate(ROUTES(lang).FEDERATED_SIGNUP)
        }
      })
      .catch((e) => {
        const accessId =
          resellerAccessId || getSessionStorage(RESELLER_ACCESS_ID)

        const providerIdentity = accessId || (accessId && accessId.length > 0)

        if (providerIdentity) {
          Auth.federatedSignIn({ customProvider: providerIdentity }).catch(
            () => {
              setError(fvData.federated_verification_error)
            }
          )
        }
        if (e !== 'The user is not authenticated') {
          setError(fvData.provider_identity_error)
        }
      })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncSessionReady])

  return (
    <Layout
      data={prismicSignUpLayout.data}
      lang={lang}
      pathName="/federated-verification"
      omitClose
      scripts={[]}
    >
      <Wrapper>
        {error && <Alert severity="error" style={{ marginBottom: '20px' }}>{error.text}</Alert>}
         { sessionReceiverURL && (
          <iframe
            title="session_receiver"
            id="session_receiver"
            src={sessionReceiverURL}
            onLoad={() => setSyncSessionReady(true)}
            width="0"
            height="0"
          />
        )}
      </Wrapper>
    </Layout>
  )
}

export default FederatedVerification

export const query = graphql`
  query ($lang: String) {
    prismicSignUpLayout(lang: { eq: $lang }) {
      _previewable
      data {
        logo_white {
          alt
          gatsbyImageData(width: 133, height: 33)
        }
        logo_black {
          alt
          gatsbyImageData(width: 133, height: 33)
        }
        background_image {
          alt
          gatsbyImageData
        }
        body {
          ... on PrismicSignUpLayoutDataBodyFooterNavigation {
            items {
              nav_label {
                text
              }
              nav_link {
                text
              }
            }
          }
        }
      }
    }
    prismicFederatedVerificationPage(lang: { eq: $lang }) {
      data {
        federated_verification_error {
          text
        }
        provider_identity_error {
          text
        }
      }
    }
  }
`;